import * as moment from 'moment';
import axios from 'axios';
import Request from './request';
import Notifications from './notifications';
import StripeService from './stripeService';
import i18n from './i18n';

const snakeToCamel = str =>
  str.replace(/([-_][a-z])/g, group =>
    group.toUpperCase().replace('-', '').replace('_', ''),
  );

const SUBSCRIPTION_TYPE = {
  FREE: 'free',
  TRIAL: 'trial',
  STANDARD: 'normal',
};
const SUBSCRIPTION_TYPES = Object.values(SUBSCRIPTION_TYPE);

const PROFILE_BLOCK = {
  INFO: 'info',
  COMPANY: 'company',
  CAREER: 'career',
  EXPERIENCE: 'experience',
  HOBBIES: 'hobbies',
  SOFT_SKILLS: 'softSkills',
  HARD_SKILLS: 'hardSkills',
  LANGUAGES: 'languages',
  CONTACTS: 'contacts',
  MOODS: 'moods',
  MEDIA: 'media',
  SOCIAL_MEDIA: 'socialMedia',
  LEGAL_FINANCES: 'legalFinances',
  AVAILABILITY: 'availability',
  DOCUMENTS: 'documents',
};

const PROFILE_BLOCKS = Object.values(PROFILE_BLOCK);

const PROFILE_TAB = {
  ...PROFILE_BLOCK,
  PAYMENT: 'payment',
  SETTINGS: 'settings',
  CONNECTORS: 'connectors',
  PERFORMANCE: 'performance',
  POSTER: 'poster',
  AVAILABILITY: 'availability',
  SIGN_BY_ME: 'signedByMe',
  DOCUMENTS: 'documents',
};

const PROFILE_TABS = Object.values(PROFILE_TAB);

const CHALLENGE_TAB = {
  TIMELINE: 'timeline',
  STATS: 'stats',
  INFO: 'info',
  LEADERBOARD: 'leaderboard',
  LEADS: 'leads',
  RECOMMENDATIONS: 'recommendations',
  COURSE: 'course',
  PERFORMANCE: 'performance',
};

const CHALLENGE_TABS = Object.values(CHALLENGE_TAB);

const INBOX_MSG_TYPE = {
  INFO: 'info',
  RATING: 'rating',
  LIKE: 'like',
  COMMENT: 'comment',
  GAME_START: 'game_start',
  GAME_END: 'game_end',
  TROPHY: 'trophy',
  NEWS: 'news',
  MAGAZINE: 'magazine',
  SURVEY: 'survey',
  CUSTOM: 'custom',
  CHAT_JOINED: 'chat_joined',
  HIGHLIGHT_SURVEY_QUESTION: 'highlight_survey_question',
  EVENT: 'event',
  NEW_TIP: 'new_tip',
  PROFILING: 'profiling',
  COURSE_PENDING: 'course_pending',
  COURSES_NOT_TAKEN: 'courses_not_taken',
  HAPPY_BIRTHDAY: 'happy_birthday',
  UNLOCK_REWARD: 'unlock_reward',
  CHARITY_REPORT: 'charity_report',
  VIDEO: 'video',
  PDF: 'pdf',
  PODCAST: 'podcast',
  CALENDAR_EVENT: 'calendar_event',
  FIRST_COURSE: 'first_course',
  SURVEY_REWARDS: 'survey_rewards',
  COURSE_REWARDS: 'course_rewards',
  USER_INVITED_CREATED: 'user_invited_created',
  ACCESS_SESSION: 'access_session',
  DRIVE: 'drive',
  WALLET_WITHDRAW_APPROVED: 'wallet_withdraw_approved',
  OPPORTUNITY_NOTE: 'opportunity_note',
  OPPORTUNITY_STATUS: 'opportunity_status',
  OPPORTUNITY_DOCUMENT: 'opportunity_document',
  REFERRAL_ORDER: 'referral_order',
  REFERRAL_LEVEL_UPDATED: 'referral_level_updated',
};

const INBOX_MSG_TYPES = Object.values(INBOX_MSG_TYPE);

const INBOX_FILTER = {
  CHALLENGES: 'challenges',
  NEWS: 'info',
  FEED: 'news',
  MEDIA: 'media',
  SURVEYS: 'surveys',
};

const INBOX_FILTERS = Object.values(INBOX_FILTER);

const TROPHY_CONFIG = {
  scores: {
    translationKey: 'trophy_scores',
    imageURL: '/images/trophies/trophy_scores.png',
  },
  points: {
    translationKey: 'trophy_points',
    imageURL: '/images/trophies/trophy_points.png',
  },
  winner: {
    translationKey: 'trophy_winner',
    imageURL: '/images/trophies/trophy_winner.png',
  },
  stimulus: {
    translationKey: 'trophy_stimulus',
    imageURL: '/images/trophies/trophy_stimulus.png',
  },
  topplayer: {
    translationKey: 'trophy_top_player',
    imageURL: '/images/trophies/trophy_topplayer.png',
  },
  'e-learning_1': {
    translationKey: 'trophy_level_1',
    imageURL: '/images/trophies/trophy_level_1.svg',
  },
  'e-learning_2': {
    translationKey: 'trophy_level_2',
    imageURL: '/images/trophies/trophy_level_2.svg',
  },
  'e-learning_3': {
    translationKey: 'trophy_level_3',
    imageURL: '/images/trophies/trophy_level_3.svg',
  },
  'e-learning_4': {
    translationKey: 'trophy_level_4',
    imageURL: '/images/trophies/trophy_level_4.svg',
  },
  custom: {
    translationKey: 'trophy_custom',
    imageURL: '/images/trophies/trophy_custom.svg',
  },
};

const STEPS_CONFIG = {
  profile_info: {
    translationKey: 'profile_info',
    imageURL: '/images/journey/step-types/profile-info.svg',
  },
  profile_company: {
    translationKey: 'profile_company',
    imageURL: '/images/journey/step-types/profile-company.svg',
  },
  profile_career: {
    translationKey: 'profile_career',
    imageURL: '/images/journey/step-types/profile-career.svg',
  },
  profile_contacts: {
    translationKey: 'profile_contacts',
    imageURL: '/images/journey/step-types/profile-contacts.svg',
  },
  profile_experience: {
    translationKey: 'profile_experience',
    imageURL: '/images/journey/step-types/profile-experience.svg',
  },
  profile_hard_skills: {
    translationKey: 'profile_hardSkills',
    imageURL: '/images/journey/step-types/profile-hard-skills.svg',
  },
  profile_hobbies: {
    translationKey: 'profile_hobbies',
    imageURL: '/images/journey/step-types/profile-hobbies.svg',
  },
  profile_languages: {
    translationKey: 'profile_language',
    imageURL: '/images/journey/step-types/profile-language.svg',
  },
  profile_legal_finances: {
    translationKey: 'profile_legal',
    imageURL: '/images/journey/step-types/profile-legal.svg',
  },
  profile_media: {
    translationKey: 'profile_media',
    imageURL: '/images/journey/step-types/profile-media.svg',
  },
  profile_moods: {
    translationKey: 'profile_moods',
    imageURL: '/images/journey/step-types/profile-moods.svg',
  },
  profile_social_media: {
    translationKey: 'profile_social_media',
    imageURL: '/images/journey/step-types/profile-social.svg',
  },
  profile_soft_skills: {
    translationKey: 'profile_soft_skills',
    imageURL: '/images/journey/step-types/profile-soft-skills.svg',
  },
  survey: {
    translationKey: 'profile_survey',
    imageURL: '/images/journey/step-types/profile-survey.svg',
  },
  profile_trophy: {
    translationKey: 'profile_trophy',
    imageURL: '/images/journey/step-types/profile-trophy.svg',
  },
  task: {
    translationKey: 'task',
    imageURL: '/images/journey/step-types/task.svg',
  },
  trophy: {
    translationKey: 'trophy',
    imageURL: '/images/journey/step-types/trophy.svg',
  },
  course: {
    translationKey: 'course',
    imageURL: '/images/journey/step-types/course.svg',
  },
  game: {
    translationKey: 'challenge',
    imageURL: '/images/journey/step-types/challenge.svg',
  },
  session: {
    translationKey: 'session',
    imageURL: '/images/journey/step-types/session.svg',
  },
  signature: {
    translationKey: 'signature',
    imageURL: '/images/journey/step-types/signature.svg',
  },
  market_product_purchase: {
    translationKey: 'purchase',
    imageURL: '/images/journey/step-types/purchase.svg',
  },
  fileUpload: {
    translationKey: 'fileUpload',
    imageURL: '/images/journey/step-types/fileUpload.svg',
  },
  document: {
    translationKey: 'fileUpload',
    imageURL: '/images/journey/step-types/fileUpload.svg',
  },
  fill_and_sign: {
    translationKey: 'fill_and_sign',
    imageURL: '/images/journey/step-types/fill_and_sign.svg',
  },
};

const GAME_TYPE = {
  PRODUCTIVITY: 'productivity',
  CROSS_SELLING: 'cross-selling',
  PROFILING: 'profiling',
  E_LEARNING: 'e-learning',
  CANDIDATES: 'candidates',
};

const GAME_TYPES = Object.values(GAME_TYPE);

const GAME_STATUS = {
  UNSTARTED: 'unstarted',
  ON_GOING: 'ongoing',
  FINISHED: 'finished',
  PERMANENT: 'permanent',
  NONE: '',
};

const GAME_STATUSES = Object.values(GAME_STATUS);

const GAME_EVENT_TYPE = {
  INFO: 'info',
  RANKING_POSITION: 'raking_position',
  GAME_START: 'game_start',
  GOAL_80: 'goal_80',
  GOAL_90: 'goal_90',
  GOAL_100: 'goal_100',
  GAME_WIN: 'game_win',
  TROPHY: 'trophy',
  LEAD_CREATED: 'lead_created',
  LEAD_CHANGED_OWNER: 'lead_changed_owner',
  LEAD_ATTACH_OWNER: 'lead_attach_owner',
  LEAD_NO_OWNER: 'lead_no_owner',
  LEAD_CHANGED: 'lead_changed',
  LEAD_WON: 'lead_won',
  LEAD_LOST: 'lead_lost',
};

const GAME_EVENT_TYPES = Object.values(GAME_EVENT_TYPE);

const LEAD_CONDITION = {
  LEADS: 'leads',
  NEED_DISCOVERED: 'need_discovered',
  PROPOSAL: 'proposal',
};

const LEAD_CONDITIONS = Object.values(LEAD_CONDITION);

const LEAD_STATUS = {
  NO_OWNER: 'no_owner',
  WAITING_OWNER: 'waiting_owner',
  IN_PROGRESS: 'in-progress',
  LOST: 'lost',
  WON: 'won',
};

const LEAD_STATUSES = Object.values(LEAD_STATUS);

const LEAD_DETAILS_TAB = {
  TIMELINE: 'timeline',
  INFO: 'info',
};

const LEAD_DETAILS_TABS = Object.values(LEAD_DETAILS_TAB);

const COURSE_STATUS = {
  UN_STARTED: 'un_started',
  FINISHED: 'finished',
  IN_PROGRESS: 'in_progress',
  WAITING: 'waiting',
  OPENED: 'opened',
};

const COURSE_STATUSES = Object.values(COURSE_STATUS);

const PURCHASE_STATUS = {
  FREE: 'free',
  PAID: 'paid',
  UNPAID: 'unpaid',
};

const isEnrolled = item =>
  item.purchaseStatus === PURCHASE_STATUS.PAID ||
  item.status === COURSE_STATUS.IN_PROGRESS ||
  item.status === COURSE_STATUS.FINISHED;

const isEnrolledOrFree = (loggedUser, item) => {
  if (item.isContentAccessible === false) {
    return false;
  }
  const hasPromotion = !!item.userPromotionPrice;
  const isFreeWithPromotion =
    !item.userPromotionPrice || !item.userPromotionPrice.price;
  const isFreeWithoutPromotion = !item.userPrice || !item.userPrice.price;
  const isFree = hasPromotion ? isFreeWithPromotion : isFreeWithoutPromotion;
  if (loggedUser) {
    return isEnrolled(item) || isFree;
  }
  return false;
};

const VOUCHER_STATUS = {
  EXPIRED: 'expired',
  USED: 'used',
  REVOKED: 'revoked',
  ACTIVE: 'active',
};

const VOUCHER_STATUSES = Object.values(VOUCHER_STATUS);

const COURSE_PROVIDER = {
  BRAVON: 'bravon',
  SCORM: 'scorm',
};

const COURSE_PROVIDERS = Object.values(COURSE_PROVIDER);

const MODULE_TYPE = {
  LESSONS: 'lessons',
  TROPHY: 'trophy',
  CERTIFICATE: 'certificate',
  INFO: 'info',
};

const MODULE_TYPES = Object.values(MODULE_TYPE);

const MODULE_STATUS = {
  DONE: 'done',
  LOCKED: 'locked',
  UNLOCKED: 'unlocked',
  IN_PROGRESS: 'in_progress',
};

const MODULE_STATUSES = Object.values(MODULE_STATUS);

const LESSON_TYPE = {
  VIDEO: 'video',
  PDF: 'pdf',
  IMAGES: 'images',
  QUIZ: 'quiz',
  YOUTUBE: 'youtube',
  WEBINAR: 'webinar',
  ZOOM: 'zoom',
  SURVEY: 'survey',
};

const LESSON_TYPES = Object.values(LESSON_TYPE);

const LESSON_CONTENT_TYPE = {
  VIDEO: 'video',
  PDF: 'pdf',
  IMAGE: 'image',
  MOTION: 'motion',
  WHITEBOARD: 'whiteboard',
  INTERVIEW: 'interview',
};

const LESSON_CONTENT_TYPES = Object.values(LESSON_CONTENT_TYPE);

const LESSON_STATUS = {
  DONE: 'done',
  LOCKED: 'locked',
  UNLOCKED: 'unlocked',
};

const LESSON_STATUSES = Object.values(LESSON_STATUS);

const QUESTION_STATUS = {
  CORRECT: 'correct',
  INCORRECT: 'incorrect',
  NOT_ANSWERED: 'not_answered',
};

const QUESTION_STATUSES = Object.values(QUESTION_STATUS);

const JOURNEY_STATUS = {
  UN_STARTED: 'un_started',
  IN_PROGRESS: 'in_progress',
  FINISHED: 'finished',
};

const JOURNEY_STATUSES = Object.values(JOURNEY_STATUS);

const JOURNEY_STEP_STATUS = {
  UNLOCKED: 'unlocked',
  LOCKED: 'locked',
  IN_PROGRESS: 'in_progress',
  DONE: 'done',
  WAITING_APPROVAL: 'waiting_approval',
  REJECTED: 'rejected',
};

const JOURNEY_STEP_STATUSES = Object.values(JOURNEY_STEP_STATUS);

const JOURNEY_TASK_STATUS = {
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected',
};

const JOURNEY_TASK_STATUSES = Object.values(JOURNEY_TASK_STATUS);

const JOURNEY_STEP_TYPE = {
  TASK: 'task',
  GAME: 'game',
  COURSE: 'course',
  SURVEY: 'survey',
  TROPHY: 'trophy',
  CERTIFICATE: 'certificate',
  PROFILE_INFO: 'profile_info',
  PROFILE_COMPANY: 'profile_company',
  PROFILE_CAREER: 'profile_career',
  PROFILE_EXPERIENCE: 'profile_experience',
  PROFILE_HOBBIES: 'profile_hobbies',
  PROFILE_SOFT_SKILLS: 'profile_soft_skills',
  PROFILE_HARD_SKILLS: 'profile_hard_skills',
  PROFILE_LANGUAGES: 'profile_languages',
  PROFILE_CONTACTS: 'profile_contacts',
  PROFILE_MOODS: 'profile_moods',
  PROFILE_MEDIA: 'profile_media',
  PROFILE_SOCIAL_MEDIA: 'profile_social_media',
  PROFILE_LEGAL_FINANCES: 'profile_legal_finances',
  PROFILE_DOCUMENTS: 'profile_documents',
  SIGNATURE: 'signature',
  MARKET_PRODUCT_PURCHASE: 'market_product_purchase',
  SESSION: 'session',
  DOCUMENT: 'document',
  FILL_AND_SIGN: 'fill_and_sign',
};

const JOURNEY_STEP_TYPES = Object.values(JOURNEY_STEP_TYPE);

// TODO: this should be done server side on JourneyManager,
// in a deserialization function like other entities have, we just need
// a good way of passing the available services into the manager, probably the constructor
const normalizeJourney = (services, journey) => {
  const disabledBlocks =
    services && services.me && services.me.config && services.me.config.disabled
      ? services.me.config.disabled
      : [];
  const steps = (journey.steps || [])
    .filter(step => {
      if (step.type === JOURNEY_STEP_TYPE.GAME) {
        return !(
          services &&
          services.perform &&
          services.perform.enabled === false
        );
      }
      if (step.type === JOURNEY_STEP_TYPE.SURVEY) {
        return !(
          services &&
          services.survey &&
          services.survey.enabled === false
        );
      }
      if (step.type.startsWith('profile_')) {
        const blockName = snakeToCamel(step.type.slice(8));
        return !disabledBlocks.includes(blockName);
      }
      return true;
    })
    .map(step => {
      let { status } = step;

      if (
        step.type !== JOURNEY_STEP_TYPE.TROPHY &&
        step.status === JOURNEY_STEP_STATUS.LOCKED &&
        !journey.isSequential
      ) {
        status = JOURNEY_STEP_STATUS.UNLOCKED;
      }

      if (
        step.type === JOURNEY_STEP_TYPE.TASK ||
        step.type === JOURNEY_STEP_TYPE.SIGNATURE ||
        step.type === JOURNEY_STEP_TYPE.DOCUMENT
      ) {
        switch (step.taskStatus) {
          case JOURNEY_TASK_STATUS.APPROVED:
            status = JOURNEY_STEP_STATUS.DONE;
            break;
          case JOURNEY_TASK_STATUS.PENDING:
            status = JOURNEY_STEP_STATUS.WAITING_APPROVAL;
            break;
          case JOURNEY_TASK_STATUS.REJECTED:
            status = JOURNEY_STEP_STATUS.REJECTED;
            break;
          default:
        }
      }

      if (step.type === JOURNEY_STEP_TYPE.CERTIFICATE) {
        if (step.certificate) {
          status = JOURNEY_STEP_STATUS.UNLOCKED;
        } else {
          status = JOURNEY_STEP_STATUS.LOCKED;
        }
      }

      return { ...step, status };
    });
  return { ...journey, steps };
};

const SURVEY_TYPE = {
  SURVEY: 'survey',
  EVENT: 'event',
};

const SURVEY_TYPES = Object.values(SURVEY_TYPE);

const SURVEY_QUESTION_TYPE = {
  CHOICE: 'choice',
  OPTIONS: 'options',
  MULTIPLE_ANSWERS: 'multiple_answers',
  EVALUATION: 'evaluation',
  OPINION: 'opinion',
};

const SURVEY_QUESTION_TYPES = Object.values(SURVEY_QUESTION_TYPE);

const WORK_TIME = {
  H7_16: '7h-16h',
  H8_17: '8h-17h',
  H9_18: '9h-18h',
  H10_19: '10-19h',
  OTHER: 'other',
};

const WORK_TIMES = Object.values(WORK_TIME);

const VEHICLE = {
  ON_FOOT: 'on_foot',
  BICYCLE: 'bicycle',
  MOTORCYCLE: 'motorcycle',
  CAR: 'car',
  TRUCK: 'truck',
  BUS: 'bus',
  TAXI: 'taxi',
  SUBWAY: 'subway',
  TRAIN: 'train',
  SHIP: 'ship',
  PLANE: 'plane',
};

const VEHICLES = Object.values(VEHICLE);

const IN_APP_PRODUCT_TYPE = {
  POSTER: 'poster',
  CERTIFICATE: 'certificate',
  DIGITAL_CERTIFICATE: 'digital_certificate',
};

const IN_APP_PRODUCT_TYPES = Object.values(IN_APP_PRODUCT_TYPE);

const TOPIC_CHAPTER = {
  WALL: 'wall',
  PERFORM: 'perform',
  ACADEMY: 'academy',
  WHOSWHO: 'whoswho',
};

const TOPIC_CHAPTERS = Object.values(TOPIC_CHAPTER);

const TOPIC_TYPE = {
  TEXT: 'text',
  BANNER: 'banner',
};

const TOPIC_TYPES = Object.values(TOPIC_TYPE);

const SOCIAL_MEDIA_CONFIG = {
  facebook: { icon: 'socicon-facebook', name: 'Facebook' },
  instagram: { icon: 'socicon-instagram', name: 'Instagram' },
  linkedin: { icon: 'socicon-linkedin', name: 'Linked In' },
  periscope: { icon: 'socicon-periscope', name: 'Periscope' },
  twitter: { icon: 'socicon-twitter', name: 'Twitter' },
  weibo: { icon: 'socicon-weibo', name: 'Weibo' },
  youtube: { icon: 'socicon-youtube', name: 'YouTube' },
};

const SOCIAL_MEDIA_SITE = {
  FACEBOOK: 'facebook',
  INSTAGRAM: 'instagram',
  LINKEDIN: 'linkedin',
  PERISCOPE: 'periscope',
  TWITTER: 'twitter',
  WEIBO: 'weibo',
  YOUTUBE: 'youtube',
};

const SOCIAL_MEDIA_SITES = Object.values(SOCIAL_MEDIA_SITE);

const ACADEMY_LIST_ITEM_TYPE = {
  JOURNEY: 'journey',
  COURSE: 'course',
  SESSION: 'session',
};

const ACADEMY_LIST_ITEM_TYPES = Object.values(ACADEMY_LIST_ITEM_TYPE);

const USER_DOC_TYPE = {
  NONE: 'none',
  NIB_VALIDATION: 'nib_validation',
  EMPLOYEE_FORM: 'employee_form',
  ADDITIONAL_IDENTITY: 'addicional_identity',
};

const SUBSCRIPTION_STATUS = {
  ACTIVE: 'active',
  PENDING_PAYMENT: 'pending_payment',
  CANCELED: 'canceled',
  FAILED: 'failed',
  OUT_OF_STOCK: 'out_of_stock',
};
const PLAN_STATUS = {
  ACTIVE: 'active',
  FAILED: 'failed',
  FAILED_AND_EXPIRED: 'failed_and_expired',
  CANCELED: 'canceled',
  PAST_DUE: 'past_due',
  UNPAID: 'unpaid',
};

const USER_DOC_TYPES = Object.values(USER_DOC_TYPE);

const ORDER_STATUSES = [
  { status: 'pending_payment', text: 'pending_payment', order: 1 },
  { status: 'pending', text: 'pending', order: 2 },
  { status: 'processing', text: 'processing', order: 3 },
  { status: 'completed', text: 'completed', order: 4 },
  {
    status: 'processing_error',
    text: 'processing_error',
    order: 5,
  },
  { status: 'out_of_stock', text: 'out_of_stock', order: 6 },
  { status: 'canceled', text: 'canceled', order: 7 },
  { status: 'refunded', text: 'refunded', order: 8 },
  { status: 'failed', text: 'failed', order: 9 },
  { status: 'lost', text: 'lost', order: 10 },
];

const SUBSCRIPTION_STATUSES = [
  { status: 'pending_payment', text: 'pending_payment', order: 1 },
  { status: 'pending', text: 'pending', order: 2 },
  { status: 'processing', text: 'pending', order: 3 },
  { status: 'processing_error', text: 'processing_error', order: 4 },
  { status: 'active', text: 'active', order: 5 },
  { status: 'completed', text: 'completed', order: 6 },
  { status: 'canceled', text: 'canceled', order: 7 },
  { status: 'refunded', text: 'refunded', order: 8 },
  { status: 'failed', text: 'failed', order: 9 },
  { status: 'out_of_stock', text: 'out_of_stock', order: 10 },
  { status: 'lost', text: 'lost', order: 11 },
];

const PLAN_STATUSES = [
  { status: 'active', text: 'active', order: 1 },
  { status: 'past_due', text: 'past_due', order: 2 },
  { status: 'unpaid', text: 'unpaid', order: 4 },
  { status: 'failed', text: 'failed', order: 5 },
  { status: 'failed_and_expired', text: 'failed_and_expired', order: 6 },
  { status: 'canceled', text: 'canceled', order: 7 },
];

const ACADEMY_ORDER_STATUSES = [
  { status: 'pending_payment', text: 'pending_payment', order: 1 },
  { status: 'completed', text: 'completed', order: 5 },
  { status: 'canceled', text: 'canceled', order: 6 },
  { status: 'refunded', text: 'refunded', order: 7 },
  { status: 'failed', text: 'failed', order: 8 },
];

const ORDER_HISTORY_TYPES = [
  { type: 'status_change', text: 'order_status_change', order: 1 },
  { type: 'payment_method_change', text: 'payment_method_change', order: 4 },
  { type: 'refund', text: 'order_refund', order: 2 },
  { type: 'rma_add', text: 'order_rma_add', order: 3 },
  { type: 'rma_update', text: 'order_rma_update', order: 10 },
];

const BUYER_TYPE = {
  OWNER: 'owner',
  SELLER: 'seller',
  CLIENTS: 'clients',
};

const BUYER_TYPES = Object.values(BUYER_TYPE);

const PRODUCT_TYPE = {
  PRODUCT: 'product',
  GIFT: 'gift',
  PACK: 'pack',
  DOWNLOADABLE: 'downloadable',
  SERVICES: 'services',
  WORKSHOPS: 'workshops',
  LICENSES: 'licenses',
};

const PRODUCT_TYPES = Object.values(PRODUCT_TYPE);

const SESSION_STATUS = {
  FINISHED: 'finished',
  LEAD: 'lead',
  CLOSED: 'closed',
  ONGOING: 'ongoing',
  PLANNED: 'planned',
  CONFIRMED: 'confirmed',
  UNSTARTED: 'un_started',
  CANCELED: 'canceled',
};

const SESSION_STATUSES = Object.values(SESSION_STATUS);

const SESSION_TYPE = {
  ONLINE: 'online',
  WEBINAR: 'webinar',
  CLASSROOM: 'classroom',
};

const SESSION_TYPES = Object.values(SESSION_TYPE);

const PAYMENT_METHOD = {
  CREDIT_CARD: 'credit_card',
  CREDIT_CARD_SOURCE: 'credit_card_source',
  BANK_TRANSFER: 'bank_transfer_manual',
  BANCONTACT: 'bancontact',
  PAID_BY_COMPANY: 'paid_by_company',
  APPLE_PAY: 'apple_pay',
  GOOGLE_PAY: 'google_pay',
  CHEQUE: 'cheque',
  CRYPTO_MANUAL: 'crypto_manual',
  PAYPAL: 'paypal',
};

const PAYMENT_METHODS = Object.values(PAYMENT_METHOD);

const SESSION_MODE = {
  PRIVATE: 'private',
  PUBLIC: 'public',
};

const ORDER_STATUS_ENUM = {
  PENDING: 'pending',
  PENDING_PAYMENT: 'pending_payment',
  PROCESSING: 'processing',
  PROCESSING_ERROR: 'processing_error',
  COMPLETED: 'completed',
  CANCELED: 'canceled',
  REFUNDED: 'refunded',
  FAILED: 'failed',
  LOST: 'lost',
};

const numberWithSpaces = x =>
  x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

const displayNumber = number => {
  if (number === '') {
    return '';
  }

  try {
    let value = numberWithSpaces(Number(number).toFixed(2));

    const decimalPlaces = value.split('.');
    if (decimalPlaces[1]) {
      value = decimalPlaces[1].length < 2 ? `${value}0` : value;
    } else {
      value = `${value}.00`;
    }

    let lang = 'en';
    if (document) {
      const data = document.querySelector('script[data-lang]');
      if (data) {
        lang = data.getAttribute('data-lang');
      }
    }
    if (lang && lang.startsWith('fr')) {
      value = value.replace('.', ',');
    }

    return value;
  } catch (err) {
    return '';
  }
};

const displayMoney = (number, symbol = '€') => {
  try {
    const numberValue = Number(number) / 100;
    const value = displayNumber(numberValue);
    return value ? `${symbol}${value}` : value;
  } catch (err) {
    return '';
  }
};

const displayUserName = (user, showUserName = false) => {
  if (!user) {
    return '';
  }
  if (!showUserName && user.firstName && user.lastName) {
    return `${user.firstName} ${user.lastName}`;
  }
  return user.username;
};

const displayRegionalAddress = address => {
  if (!address) {
    return '';
  }
  const state = address.state ? ` ${address.state}` : '';
  return `${address.zipCode}${address.state ? ` ${state}` : ''} ${
    address.city
  }`;
};

// Add the optional fields in billing address
const handleBillingAddressInOrder = (user, address) => {
  const billingAddress = address;
  // Add the optionals fields to billing address
  if (user?.address?.firstName) {
    billingAddress.firstName = user.address?.firstName;
  } else if (user?.firstName) {
    billingAddress.firstName = user.firstName;
  }
  if (user?.address?.lastName) {
    billingAddress.lastName = user.address?.lastName;
  } else if (user?.lastName) {
    billingAddress.lastName = user.lastName;
  }
  if (user?.address?.email) {
    billingAddress.email = user.address?.email;
  } else if (user?.email) {
    billingAddress.email = user.email;
  }
  if (user?.address?.phone) {
    billingAddress.phone = user.address?.phone;
  } else if (user?.phone) {
    billingAddress.phone = user.phone;
  }
  return billingAddress;
};
// Check if the optional fields don't exist in shipping address
const handleShippingAddressInOrder = (user, shippingAddress) => {
  const shippingAddressUpdated = shippingAddress;
  if (shippingAddress && user) {
    if (!shippingAddress.firstName) {
      shippingAddressUpdated.firstName = user?.firstName;
    }
    if (!shippingAddress.lastName) {
      shippingAddressUpdated.lastName = user?.lastName;
    }
    if (!shippingAddress.email) {
      shippingAddressUpdated.email = user?.email;
    }
    if (!shippingAddress.phone) {
      shippingAddressUpdated.phone = user?.phone;
    }
  }
  return shippingAddressUpdated;
};

const displayTrophyName = (t, trophy) => {
  const finalTrophy = trophy || {};
  let name;
  ({ name } = finalTrophy);
  if (finalTrophy.type === 'custom' && finalTrophy.trophyCustom) {
    ({ name } = finalTrophy.trophyCustom);
  } else if (trophy.type && TROPHY_CONFIG[finalTrophy.type]) {
    name = t(TROPHY_CONFIG[finalTrophy.type].translationKey);
  } else if (trophy.level && TROPHY_CONFIG[`e-learning_${trophy.level}`]) {
    name = t(TROPHY_CONFIG[`e-learning_${trophy.level}`].translationKey);
  }
  return name;
};

const debounce = (func, delay) => {
  let inDebounce;
  return function debounced(...args) {
    const context = this;
    clearTimeout(inDebounce);
    inDebounce = setTimeout(() => {
      func.apply(context, args);
    }, delay);
  };
};

const throttle = (func, limit) => {
  let lastFunc;
  let lastRan;
  return function throttled(...args) {
    const context = this;
    if (!lastRan) {
      func.apply(context, args);
      lastRan = Date.now();
    } else {
      clearTimeout(lastFunc);
      lastFunc = setTimeout(() => {
        if (Date.now() - lastRan >= limit) {
          func.apply(context, args);
          lastRan = Date.now();
        }
      }, limit - (Date.now() - lastRan));
    }
  };
};

const compose = (fn, ...rest) =>
  rest.length === 0 ? fn : (...args) => fn(compose(...rest)(...args));

const displayCardNumber = last4 => `**** **** **** ${last4}`;

const colors = colorParam => {
  const color = colorParam === 'primary' ? 'dynamic' : colorParam;

  const fallback = {
    dynamic: '#abb7b6',
    inverse: '#ebedf2',
    secondary: '#ebedf2',
    metal: '#c4c5d6',
    light: '#ffffff',
    accent: '#00c5dc',
    success: '#34bfa3',
    info: '#36a3f7',
    warning: '#ffb822',
    danger: '#f4516c',
    focus: '#9816f4',
  };
  if (typeof window !== 'undefined' && window.getComputedStyle) {
    const cssVal = window
      .getComputedStyle(document.documentElement)
      .getPropertyValue(`--${color}`);
    return cssVal || fallback[color];
  }
  return fallback[color];
};

const addAlphaToHex = (hex, opacity) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);

  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

const hexToHSL = hex => {
  // Convert hex to RGB first
  let r = 0;
  let g = 0;
  let b = 0;

  if (hex.length === 4) {
    r = `0x${hex[1]}${hex[1]}`;
    g = `0x${hex[2]}${hex[2]}`;
    b = `0x${hex[3]}${hex[3]}`;
  } else if (hex.length === 7) {
    r = `0x${hex[1]}${hex[2]}`;
    g = `0x${hex[3]}${hex[4]}`;
    b = `0x${hex[5]}${hex[6]}`;
  }

  // Then to HSL
  r /= 255;
  g /= 255;
  b /= 255;
  const cmin = Math.min(r, g, b);
  const cmax = Math.max(r, g, b);
  const delta = cmax - cmin;
  let h = 0;
  let s;
  let l;

  if (delta === 0) {
    h = 0;
  } else if (cmax === r) {
    h = ((g - b) / delta) % 6;
  } else if (cmax === g) {
    h = (b - r) / delta + 2;
  } else {
    h = (r - g) / delta + 4;
  }

  h = Math.round(h * 60);

  if (h < 0) {
    h += 360;
  }

  l = (cmax + cmin) / 2;
  s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));
  s = +(s * 100).toFixed(1);
  l = +(l * 100).toFixed(1);

  return { h, s, l };
};

const hslToHex = (pH, pS, pl) => {
  const h = pH / 360;
  const s = pS / 100;
  const l = pl / 100;
  let r;
  let g;
  let b;

  if (s === 0) {
    r = l;
    g = l;
    b = l;
  } else {
    const hue2rgb = (p, q, t) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }
  const toHex = x => {
    const hex = Math.round(x * 255).toString(16);
    return hex.length === 1 ? `0${hex}` : hex;
  };
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

const changeLightness = (amount, color) => {
  if (!color) {
    return '';
  }
  const { h, s, l } = hexToHSL(color);
  return hslToHex(h, s, l * amount);
};

const handleImageUpload = (t, _csrf, imageState, isVideo) =>
  new Promise(resolve => {
    if (!imageState || !imageState.blob) {
      resolve(null);
    } else {
      const { fileName, blob } = imageState;
      const formData = new FormData();
      formData.append('file', blob, fileName);
      formData.append('upload_file', true);
      const url = isVideo ? '/upload/video' : '/upload/image';

      new Request(_csrf)
        .formMulti('POST', url, formData)
        .done(result => resolve(result.data))
        .fail(err => {
          const error = isVideo ? 'error_videoUpload' : 'error_imageUpload';
          Notifications.showErrorFromRequest(err, error);
        });
    }
  });

const partitionArray = (array, partitionSize) =>
  Array(Math.ceil(array.length / partitionSize))
    .fill(0)
    .map((_, index) => index * partitionSize)
    .map(begin => array.slice(begin, begin + partitionSize));

const getSizeFromString = sizeString => {
  const SIZE_REGEX = /^([0-9]+\.?[0-9]+)px$/;
  const matches = sizeString.match(SIZE_REGEX);
  if (!matches || matches.length < 2) {
    return 0;
  }
  return Number(matches[1]);
};

const getWidthForElement = element => {
  const style = getComputedStyle(element);
  const [width, margin] = ['width', 'margin-right'].map(attr =>
    getSizeFromString(style[attr]),
  );
  return width + margin * 2;
};

const formatDateTime = (time, format = "DD MMM, 'YY - HH:mm") =>
  moment.utc(time).format(format);

const formatDate = (time, format = "DD MMM, 'YY") =>
  moment.utc(time).format(format);

const dateWithoutTimeZone = date => {
  let dateWithoutZone = date;
  if (!date.isUtc()) {
    const zone = date.utcOffset();
    dateWithoutZone = date.add(zone, 'minutes');
    dateWithoutZone = dateWithoutZone.utcOffset(0);
  }
  return dateWithoutZone;
};

const uuid = () =>
  'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    // eslint-disable-next-line no-bitwise
    const r = (Math.random() * 16) | 0;
    // eslint-disable-next-line no-bitwise
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });

const linkify = text => {
  const urlRegex =
    /(\b(https?|ftp|file):\/\/[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|])/gi;
  return text.replace(urlRegex, url => `<a href="${url}">${url}</a>`);
};
const findCurrencySymbol = (code, currencies) =>
  currencies.find(c => c.code === code.toUpperCase()).symbol;
const formatPrice = (centsValue, symbol) =>
  `${(Math.round(centsValue) / 100).toFixed(2)}${symbol}`;
const getNavigatorLanguage = () => {
  if (typeof window !== 'undefined') {
    if (navigator) {
      return navigator.language;
    }
  }
  return undefined;
};
const newFormatPrice = (number, isCents, code, isFloor) => {
  let value = Number(number);
  if (Number.isNaN(value)) return '';

  if (isCents) {
    value = isFloor ? Math.floor(number) / 100 : Math.round(number) / 100;
  }

  let strValue = `${value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    currency: code ? code.toUpperCase() : 'EUR',
    style: 'currency',
  })}`;
  strValue = strValue.replace(/,/g, ' ');
  return strValue;
};

const formatNumbers = (number, isCents = false, hasDecimalPlaces = true) => {
  if (typeof number !== 'number') return '-';
  let value = number;
  if (isCents) {
    value = Math.round(number / 100);
  }
  let options = {};
  if (hasDecimalPlaces) {
    options = { minimumFractionDigits: 2, maximumFractionDigits: 2 };
  }

  let strValue = value.toLocaleString('en-US', options);
  strValue = strValue.replace(/,/g, ' ');
  return strValue;
};

const formatNumbersWithZero = (
  number,
  isCents = false,
  hasDecimalPlaces = true,
) => {
  if (number === 0) return number;
  return formatNumbers(number, isCents, hasDecimalPlaces);
};

const getSeparator = separatorType => {
  const numberWithGroupAndDecimalSeparator = 10000.1;
  const separatorsArray = Intl.NumberFormat('en-US').formatToParts(
    numberWithGroupAndDecimalSeparator,
  );

  // Adapted it to our format decimal = "." group (thousands) = " "
  const ourSeparator = separatorsArray.map(s => {
    let separatorItem = s;
    if (s.type === 'group') {
      separatorItem = { type: s.type, value: ' ' };
    }
    return separatorItem;
  });

  const separator = ourSeparator.find(
    part => part.type === separatorType,
  ).value;
  return separator;
};
const showProgress = progress => {
  if (progress % 1 > 0) {
    return progress.toFixed(2);
  }
  return progress;
};

const getLink = url => {
  if (url && (url.startsWith('https://') || url.startsWith('http://'))) {
    return url;
  }
  return `https://${url}`;
};

const formatURL = url => {
  if (!url) {
    return '';
  }
  if (url.endsWith('/')) {
    return url;
  }
  return `${url}/`;
};

const handleStripePayment = async (paymentMethod, secret) =>
  StripeService.handlePaymentConfirmation(paymentMethod, secret);

const encodeString = obj => new Buffer(JSON.stringify(obj)).toString('base64');
const decodeString = obj => {
  const buff = new Buffer(obj, 'base64');
  return JSON.parse(buff);
};

// DECODE COOKIES
const parseCookies = request => {
  const list = {};
  const cookieHeader = request && request.headers && request.headers.cookie;
  if (!cookieHeader) return list;

  cookieHeader.split(';').forEach(cookie => {
    // eslint-disable-next-line prefer-const
    let [name, ...rest] = cookie.split('=');
    name = name && name.trim();
    if (!name) return;
    const value = rest.join('=').trim();
    if (!value) return;
    list[name] = decodeURIComponent(value);
  });

  return list;
};

// Responsible to add the values saved in the cookies
const returnParams = (req, previousParams = {}, notforceCountry = null) => {
  const { localCurrency, localCountry, localLanguage } = parseCookies(req);
  const params = previousParams;
  if (localCurrency) {
    params.currency = localCurrency;
  }
  if (localCountry && !notforceCountry) {
    params.country = localCountry;
  }
  // some filters use the param language, so we need to be careful
  if (!req.user && localLanguage) {
    params.language = localLanguage;
  }
  return params;
};

const extractLanguageFromCookies = req => {
  const { localLanguage } = parseCookies(req);
  return localLanguage;
};

const gameStatus = (startDate, endDate) => {
  if (startDate && endDate) {
    const today = moment().valueOf();

    if (today < startDate) {
      return 'unstarted';
    }
    if (today > startDate && today < endDate) {
      return 'ongoing';
    }
    if (today > endDate) {
      return 'finished';
    }
    return '';
  }
  return 'permanent';
};

const getBrowserFullscreenElementProp = () => {
  if (typeof document.fullscreenElement !== 'undefined') {
    return 'fullscreenElement';
  }
  if (typeof document.mozFullScreenElement !== 'undefined') {
    return 'mozFullScreenElement';
  }
  if (typeof document.msFullscreenElement !== 'undefined') {
    return 'msFullscreenElement';
  }
  if (typeof document.webkitFullscreenElement !== 'undefined') {
    return 'webkitFullscreenElement';
  }
  return null;
};
const handleClickFullScreen = id => {
  const fullscreenMode = getBrowserFullscreenElementProp();
  switch (fullscreenMode) {
    case 'fullscreenElement':
      document.getElementById(id).requestFullscreen();
      break;
    case 'mozFullScreenElement' /* Firefox */:
      document.getElementById(id).mozFullScreenElement();
      break;
    case 'msFullscreenElement' /* IE11 */:
      document.getElementById(id).msRequestFullscreen();
      break;
    case 'webkitFullscreenElement' /* Safari */:
      document.getElementById(id).webkitRequestFullscreen();
      break;
    default:
      throw new Error('fullscreenElement is not supported by this browser');
  }
};

const returnAddress = addressParam => {
  let addressComposed = '';
  if (addressParam.address_1) {
    addressComposed += addressParam.address_1;
  }
  if (addressParam.address_2) {
    addressComposed += `, ${addressParam?.address_2}`;
  }
  if (addressParam.zipCode) {
    addressComposed += `, ${addressParam?.zipCode}`;
  }
  if (addressParam.city) {
    addressComposed += `, ${addressParam?.city}`;
  }
  if (addressParam.country) {
    addressComposed += `, ${addressParam?.country}`;
  }
  return addressComposed;
};

// FOR PDF
const handleUploadFile = async (
  _csrf,
  event,
  dataParam = null,
  fileId = null,
) => {
  let data;
  let originalName;
  let size;
  let ext;
  if (!dataParam) {
    const file = event.target.files[0];

    originalName = file.name;
    const { size: sizeParam } = file;
    const nameParts = originalName.split('.');
    const extParam = `.${nameParts[nameParts.length - 1]}`;
    size = sizeParam;
    ext = extParam;
    data = new FormData();
    data.append('file', file);
  } else {
    data = dataParam.formData;
    size = dataParam.size || null;
    ext = dataParam.ext || null;
    originalName = dataParam.originalName || null;
  }

  let endpoint = '/upload/file';
  if (fileId) {
    endpoint += `?id=${fileId}`;
  }

  const { data: response } = await axios.post(endpoint, data, {
    headers: {
      'Accept-Version': '1.0.0',
      'X-Gamification-API': 'Games',
      'X-CSRF-Token': _csrf,
      _csrf,
    },
  });
  const newNameParts = response.data.split('/');
  const newName = newNameParts[newNameParts.length - 1];

  return {
    url: response.data,
    name: newName,
    originalName: originalName || newName,
    ext,
    size,
  };
};

const RewardTypes = {
  COINS: 'coins',
  CHARITY_COINS: 'charityCoins',
  COMMISSION: 'commission',
  COMMISSION_VA: 'commissionVA',
  COMMISSION_CURRENCY: 'commissionCurrency',
  COUPONS: 'coupons',
  VOUCHERS: 'vouchers',
  WALLET_BALANCE: 'walletBalance',
};

const returnAcademyContext = (url, journeyContext, sessionContext) => {
  let path = url;
  if (journeyContext && !sessionContext) {
    path += `?journeyContext=${journeyContext}`;
  } else if (sessionContext && !journeyContext) {
    path += `?sessionContext=${sessionContext}`;
  } else if (sessionContext && journeyContext) {
    path += `?sessionContext=${sessionContext}&journeyContext=${journeyContext}`;
  }
  return path;
};

const checkStripeKeys = loggedUser => {
  const hasCustomerIdKey = loggedUser?.stripe?.customerIdPerKey;
  if (!hasCustomerIdKey) {
    return true;
  }
  const stripeKeys = Object.values(loggedUser.stripe.customerIdPerKey).filter(
    s => s,
  );
  return stripeKeys.length === 0;
};

const PROMO_ERRORS = {
  CURRENT_TIME_GREATER: 'currentTimeIsGreater',
  START_DATE_GREATER: 'startDateIsGreater',
  NO_ERRORS: 'noErrors',
};

const checkPromoDate = (isEdit, accessSession) => {
  const currentTime = moment().valueOf();
  const startDateInMS = moment(
    $('#promotionPriceDateStart').val(),
    'DD MMM YYYY - HH:mm',
  ).valueOf();
  const endDateInMS = moment(
    $('#promotionPriceDateEnd').val(),
    'DD MMM YYYY - HH:mm',
  ).valueOf();

  const isEqualToInitialStartDate =
    isEdit && startDateInMS === accessSession?.promotionPriceDateStart;
  const isEqualToInitialEndDate =
    isEdit && endDateInMS === accessSession?.promotionPriceDateEnd;

  if (isEdit && isEqualToInitialStartDate && isEqualToInitialEndDate) {
    return PROMO_ERRORS.NO_ERRORS;
  }
  if (startDateInMS >= endDateInMS) {
    return PROMO_ERRORS.START_DATE_GREATER;
  }
  if (endDateInMS <= currentTime) {
    return PROMO_ERRORS.CURRENT_TIME_GREATER;
  }
  return PROMO_ERRORS.NO_ERRORS;
};

const getDifferenceBetweenDates = (startDate, endDate) => {
  const start = moment(startDate).endOf('millisecond');
  const end = moment(endDate).endOf('millisecond');

  const days = end.diff(start, 'days');
  const hours = end.diff(start, 'hours') - days * 24;

  const minutes = end.diff(start, 'minutes') - days * 24 * 60 - hours * 60;
  const seconds =
    end.diff(start, 'seconds') - days * 24 * 60 - hours * 60 - minutes * 60;

  return { days, hours, minutes, seconds };
};

const composeDate = (startDate, endDate) => {
  const { days, hours, minutes, seconds } = getDifferenceBetweenDates(
    startDate,
    endDate,
  );

  let estimatedTime = '';
  if (days > 0) {
    const daysTxt = days > 1 ? i18n.__('time.days', days) : i18n.__('time.day');
    estimatedTime += daysTxt;
  }
  if (hours > 0) {
    const hoursTxt =
      hours > 1 ? i18n.__('time.hours', hours) : i18n.__('time.hour');
    estimatedTime += days > 0 ? `, ${hoursTxt}` : hoursTxt;
  }
  if (minutes > 0) {
    const minutesTxt =
      minutes > 1 ? i18n.__('time.minutes', minutes) : i18n.__('time.minute');
    estimatedTime += hours > 0 || days > 0 ? `, ${minutesTxt}` : minutesTxt;
  }
  return { estimatedTime, days, hours, minutes, seconds };
};

const promotionEndingMessage = (
  promotionPriceDateEnd,
  customKey = 'product.promotion.days',
) => {
  const currentDate = moment().valueOf();
  if (!promotionPriceDateEnd || promotionPriceDateEnd < currentDate) {
    return '';
  }
  const { days, minutes, seconds } = getDifferenceBetweenDates(
    currentDate,
    promotionPriceDateEnd,
  );
  if (
    days < 0 ||
    minutes < 0 ||
    (days === 0 && minutes === 0 && seconds === 0) ||
    days > 4 ||
    (days === 4 && minutes === 0)
  ) {
    return '';
  }

  if (days < 1 && (minutes > 0 || (minutes === 0 && seconds > 0))) {
    return i18n.__('product.promotion.today');
  }
  const moreThatTheDay = minutes > 0 ? days + 1 : days;
  return i18n.__(customKey, moreThatTheDay);
};

const calculatePromoPercentage = (price, promoPrice) => {
  if (price && promoPrice && price > promoPrice) {
    return Math.round((promoPrice * 100) / price) || null;
  }
  return '';
};

const returnDateMsg = (days, hours, minutes) => {
  const daysStr = i18n.__('time.days', days);
  const hoursStr = i18n.__('time.hours', hours);
  const minutesStr = i18n.__('time.minutes', minutes);
  return `${days > 0 ? `${daysStr}` : ''}${
    days === 0 && hours > 0 ? `, ${hoursStr}` : ''
  }
  ${hours === 0 && minutes > 0 ? `, ${minutesStr}` : ''}`;
};

const renderPromotionMessage = (
  t,
  promotionPriceDateStart,
  promotionPriceDateEnd,
) => {
  const currentDate = moment().utc().valueOf();

  const promoStartDate =
    promotionPriceDateStart && formatDateTime(promotionPriceDateStart);
  const promoEndDate =
    promotionPriceDateEnd && formatDateTime(promotionPriceDateEnd);

  if (
    (!promotionPriceDateStart && !promotionPriceDateEnd) ||
    (!promotionPriceDateStart && currentDate > promotionPriceDateEnd) ||
    (promotionPriceDateStart &&
      promotionPriceDateEnd &&
      promotionPriceDateStart > promotionPriceDateEnd)
  ) {
    return null;
  }
  if (promoStartDate && !promoEndDate) {
    return t('product.variation.promotion.message.1', promoStartDate);
  }
  if (!promoStartDate && promoEndDate) {
    const { days, hours, minutes } = getDifferenceBetweenDates(
      currentDate,
      promotionPriceDateEnd,
    );
    return t(
      'product.variation.promotion.message.2',
      returnDateMsg(days, hours, minutes),
      promoEndDate,
    );
  }
  const { days, hours, minutes } = getDifferenceBetweenDates(
    promotionPriceDateStart,
    promotionPriceDateEnd,
  );
  return i18n.__(
    'product.variation.promotion.message.3',
    returnDateMsg(days, hours, minutes),
    promoStartDate,
  );
};

const FrequencySubscriptionTypes = {
  ONLY_ONCE: 'only_once',
  WEEKLY: 'weekly',
  MONTHLY: 'monthly',
  YEARLY: 'yearly',
  HALF_YEARLY: 'half_yearly',
};

const FREQUENCIES_TYPES = Object.values(FrequencySubscriptionTypes);

const getMimeType = ext => {
  switch (ext) {
    case 'docx':
      return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    case 'xlsx':
      return 'application/application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    case 'csv':
    default:
      return 'text/csv';
  }
};
function downloadFile(fileToDownload) {
  const { url, ext: fileExt, originalName } = fileToDownload;
  const fallbackExt = fileToDownload?.name?.split('.')
    ? fileToDownload?.name?.split('.')[1]
    : ';';
  const ext = fileExt || fallbackExt;
  const name = fileToDownload?.originalName || fileToDownload?.name || '';
  axios({
    url,
    params: {},
    method: 'get',
    responseType: 'blob',
  }).then(response => {
    const file = new Blob([response.data], { type: getMimeType(ext) });
    const fileURL = URL.createObjectURL(file);
    const a = document.createElement('A');
    a.href = fileURL;
    a.download = `${name}.${ext}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  });
}

const handleInitTab = paymentMethodsParam => {
  if (paymentMethodsParam.includes(PAYMENT_METHOD.CREDIT_CARD)) {
    return PAYMENT_METHOD.CREDIT_CARD;
  }
  if (paymentMethodsParam.includes(PAYMENT_METHOD.BANCONTACT)) {
    return PAYMENT_METHOD.BANCONTACT;
  }
  if (paymentMethodsParam.includes(PAYMENT_METHOD.BANK_TRANSFER)) {
    return PAYMENT_METHOD.BANK_TRANSFER;
  }
  if (paymentMethodsParam.includes(PAYMENT_METHOD.PAID_BY_COMPANY)) {
    return PAYMENT_METHOD.PAID_BY_COMPANY;
  }
  return PAYMENT_METHOD.BANK_TRANSFER;
};

/**
 * Check if the country code ES_AM is allowed for billingAddress countryCode of the user
 * accepts Spain (es), Canary Islands (ic), Ceuta (es-ce), Melilla (es-me)
 * returns true if it is
 * @param countryCode
 * @returns {boolean}
 */
const isBusinessTypeESAMAllowed = countryCode => {
  if (!countryCode) {
    return false;
  }
  return ['es', 'ic', 'es-ce', 'es-me'].includes(countryCode.toLowerCase());
};

export {
  SUBSCRIPTION_TYPE,
  SUBSCRIPTION_TYPES,
  PROFILE_BLOCK,
  PROFILE_BLOCKS,
  PROFILE_TAB,
  PROFILE_TABS,
  CHALLENGE_TAB,
  CHALLENGE_TABS,
  INBOX_MSG_TYPE,
  INBOX_MSG_TYPES,
  INBOX_FILTER,
  INBOX_FILTERS,
  TROPHY_CONFIG,
  GAME_TYPE,
  GAME_TYPES,
  GAME_STATUS,
  GAME_STATUSES,
  GAME_EVENT_TYPE,
  GAME_EVENT_TYPES,
  LEAD_CONDITION,
  LEAD_CONDITIONS,
  LEAD_STATUS,
  LEAD_STATUSES,
  LEAD_DETAILS_TAB,
  LEAD_DETAILS_TABS,
  COURSE_STATUS,
  COURSE_STATUSES,
  isEnrolled,
  isEnrolledOrFree,
  VOUCHER_STATUS,
  VOUCHER_STATUSES,
  COURSE_PROVIDER,
  COURSE_PROVIDERS,
  MODULE_TYPE,
  MODULE_TYPES,
  MODULE_STATUS,
  MODULE_STATUSES,
  LESSON_TYPE,
  LESSON_TYPES,
  LESSON_CONTENT_TYPE,
  LESSON_CONTENT_TYPES,
  LESSON_STATUS,
  LESSON_STATUSES,
  QUESTION_STATUS,
  QUESTION_STATUSES,
  JOURNEY_STATUS,
  JOURNEY_STATUSES,
  JOURNEY_STEP_STATUS,
  JOURNEY_STEP_STATUSES,
  JOURNEY_TASK_STATUS,
  JOURNEY_TASK_STATUSES,
  JOURNEY_STEP_TYPE,
  JOURNEY_STEP_TYPES,
  normalizeJourney,
  SURVEY_TYPE,
  SURVEY_TYPES,
  SURVEY_QUESTION_TYPE,
  SURVEY_QUESTION_TYPES,
  WORK_TIME,
  WORK_TIMES,
  VEHICLE,
  VEHICLES,
  IN_APP_PRODUCT_TYPE,
  IN_APP_PRODUCT_TYPES,
  TOPIC_CHAPTER,
  TOPIC_CHAPTERS,
  TOPIC_TYPE,
  TOPIC_TYPES,
  SOCIAL_MEDIA_CONFIG,
  SOCIAL_MEDIA_SITE,
  SOCIAL_MEDIA_SITES,
  ACADEMY_LIST_ITEM_TYPE,
  ACADEMY_LIST_ITEM_TYPES,
  USER_DOC_TYPE,
  USER_DOC_TYPES,
  SUBSCRIPTION_STATUS,
  SUBSCRIPTION_STATUSES,
  PLAN_STATUS,
  PLAN_STATUSES,
  ORDER_STATUSES,
  ORDER_HISTORY_TYPES,
  BUYER_TYPE,
  BUYER_TYPES,
  PRODUCT_TYPE,
  PRODUCT_TYPES,
  SESSION_STATUS,
  SESSION_STATUSES,
  SESSION_TYPE,
  SESSION_TYPES,
  SESSION_MODE,
  ORDER_STATUS_ENUM,
  ACADEMY_ORDER_STATUSES,
  returnAcademyContext,
  displayNumber,
  displayMoney,
  displayUserName,
  displayRegionalAddress,
  handleBillingAddressInOrder,
  handleShippingAddressInOrder,
  displayTrophyName,
  snakeToCamel,
  debounce,
  throttle,
  compose,
  colors,
  changeLightness,
  displayCardNumber,
  addAlphaToHex,
  hexToHSL,
  hslToHex,
  handleImageUpload,
  partitionArray,
  getSizeFromString,
  getWidthForElement,
  formatDate,
  formatDateTime,
  dateWithoutTimeZone,
  uuid,
  linkify,
  formatPrice,
  showProgress,
  getLink,
  findCurrencySymbol,
  handleStripePayment,
  formatNumbers,
  formatNumbersWithZero,
  newFormatPrice,
  getSeparator,
  formatURL,
  encodeString,
  decodeString,
  parseCookies,
  returnParams,
  gameStatus,
  getBrowserFullscreenElementProp,
  handleClickFullScreen,
  returnAddress,
  checkPromoDate,
  calculatePromoPercentage,
  PROMO_ERRORS,
  PAYMENT_METHODS,
  PAYMENT_METHOD,
  handleUploadFile,
  RewardTypes,
  checkStripeKeys,
  getDifferenceBetweenDates,
  promotionEndingMessage,
  renderPromotionMessage,
  FrequencySubscriptionTypes,
  FREQUENCIES_TYPES,
  STEPS_CONFIG,
  composeDate,
  downloadFile,
  extractLanguageFromCookies,
  handleInitTab,
  isBusinessTypeESAMAllowed,
};
